import type { JSX } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { PENNYLANE_ORIGIN } from './origin-filter.js';

export interface OriginBadgeProps {
  /** Code d'origine : code boutique (`fr`, `eu`…) ou `pennylane`. */
  code: string;
  /** Libellé déjà résolu par l'API. À défaut, il est cherché dans la liste des boutiques. */
  label?: string | undefined;
}

/** Badge d'origine — Pennylane se distingue des boutiques Shopify par sa teinte. */
export function OriginBadge({ code, label }: OriginBadgeProps): JSX.Element {
  const { data } = useStoresList();
  const store = (data?.items ?? []).find((item) => item.code === code);
  const isPennylane = code === PENNYLANE_ORIGIN;
  const text = label ?? (isPennylane ? 'Pennylane' : (store?.name ?? code.toUpperCase()));

  return <Badge tone="gray" className="border-dashed font-semibold">{text}</Badge>;
}

export interface OriginBadgesProps {
  /** Liste vide = toutes les origines. */
  codes: readonly string[];
  emptyLabel?: string;
}

/** Liste de badges d'origine, avec un repli explicite quand aucune n'est restreinte. */
export function OriginBadges({ codes, emptyLabel = 'Toutes' }: OriginBadgesProps): JSX.Element {
  if (codes.length === 0) {
    return <span className="text-sm text-muted-foreground">{emptyLabel}</span>;
  }

  return (
    <span className="flex flex-wrap items-center gap-1">
      {codes.map((code) => (
        <OriginBadge key={code} code={code} />
      ))}
    </span>
  );
}
